// packages/core/src/learning/core-modules/freedom-math.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 10: "Freedom Math" (tier: freedom-path)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Impact Lab links (verified present): '401k-match', 'starting-at-40'.
// Tier is 'freedom-path' (the non-legacy taxonomy value).
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'freedom-math--freedom-has-a-number',
	title: 'Freedom has a number',
	description:
		'You learn why financial freedom starts becoming real when your lifestyle has a rough target number.',
	pillar: 'freedom-math',
	schoolsOfThought: ['fi-math', 'fire-movement', 'personal-finance-basics'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['freedom-math', 'fi-number', 'expenses', 'financial-independence'],
	prerequisites: [],
	relatedLessons: ['freedom-math--savings-rate-buys-years'],
	concepts: ['fi-number', 'annual-expenses', 'freedom-target'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Financial freedom sounds vague until you give it a number. Not a perfect number. Not a magic number. A working number. The point is to stop treating freedom like a dream cloud and start treating it like a math problem you can understand.',
		},
		{
			type: 'paragraph',
			text: 'The basic idea is simple: your lifestyle costs money every year. If your invested assets could eventually support that annual cost, work becomes less forced. You may still choose to work. You may still want to earn. But the pressure changes.',
		},
		{
			type: 'callout',
			icon: '🧮',
			text: 'Freedom math starts with one question: how much does your life cost each year?',
		},
		{
			type: 'bullet_list',
			items: [
				'Monthly expenses show your current lifestyle',
				'Annual expenses show the size of the machine',
				'Your FI number gives you a rough target',
				'The target changes when your lifestyle changes',
			],
		},
		{
			type: 'example',
			label: 'Simple FI number',
			text: 'If someone’s life costs $36,000 per year, a rough 25x freedom target would be $900,000. If their life costs $60,000 per year, the rough target becomes $1.5 million. The lifestyle drives the number.',
		},
		{
			type: 'paragraph',
			text: 'This is why spending matters even when you earn more. A higher lifestyle does not only cost more today. It can also raise the amount of wealth needed to make work optional later. That does not mean low spending is always better. It means every lifestyle level has a freedom price.',
		},
		{
			type: 'paragraph',
			text: 'The number is not a prediction. Markets move. Life changes. Housing changes. Family needs change. Inflation exists. Health costs can surprise people. The number is a compass. It helps you see the connection between lifestyle, savings, investing, and future options.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is treating the FI number like a fixed destiny instead of a living estimate.',
		},
		{
			type: 'key_takeaway',
			text: 'Your FI number is not the finish line carved in stone. It is the first clear picture of what freedom might cost.',
		},
	],
	audioScript:
		'Freedom sounds motivational until you put a number on it. Then it becomes more real. Not because the number is perfect, but because it gives you something to think with. The first question is simple: how much does your life cost each year? If your monthly life costs $3,000, that is $36,000 a year. If your monthly life costs $5,000, that is $60,000 a year. That annual cost is the size of the machine you are trying to support. A common rough freedom target is 25 times annual expenses. So a $36,000 lifestyle points to a rough $900,000 target. A $60,000 lifestyle points to around $1.5 million. The point is not to obsess over the exact number. The point is to see the relationship. Your lifestyle changes your freedom math. Every recurring cost you add can raise the amount of wealth you need later. Every expense you remove can lower the target. That does not mean you should live miserably. It means you should understand the tradeoff. The FI number is a compass, not a promise. Life changes. Markets change. Your goals change. But once you know the rough number, financial freedom stops being a fantasy and starts becoming a decision map.',
	flashcards: [
		{
			id: 'freedom-math--freedom-has-a-number--fc-main',
			role: 'main_idea',
			front: 'What is an FI number?',
			back: 'A rough target for how much invested wealth could support your annual lifestyle.',
		},
		{
			id: 'freedom-math--freedom-has-a-number--fc-watch',
			role: 'watch_out',
			front: 'What changes the FI number?',
			back: 'Your lifestyle cost, assumptions, inflation, family needs, and future goals.',
		},
		{
			id: 'freedom-math--freedom-has-a-number--fc-use',
			role: 'use_it',
			front: 'What should you calculate first?',
			back: 'Estimate your annual expenses so freedom has a working target.',
		},
	],
	applyAction: {
		type: 'calculateFiNumber',
		label: 'Calculate your FI number',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'freedom-math--savings-rate-buys-years',
	title: 'Savings rate buys years',
	description:
		'You learn why savings rate matters because it controls both how fast you build wealth and how expensive your life is.',
	pillar: 'freedom-math',
	schoolsOfThought: ['fi-math', 'fire-movement', 'frugality'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['savings-rate', 'fire', 'time', 'freedom-math'],
	prerequisites: ['freedom-math--freedom-has-a-number'],
	relatedLessons: ['freedom-math--free-money-is-still-math'],
	concepts: ['savings-rate', 'time-to-freedom', 'dual-effect'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Savings rate is one of the most powerful numbers in freedom math. Not because saving is morally better than spending. Because savings rate does two jobs at the same time: it tells you how much you are building, and it hints at how expensive your life is to support.',
		},
		{
			type: 'paragraph',
			text: 'If you earn $4,000 per month after taxes and save $400, your savings rate is 10%. If you save $1,000, your savings rate is 25%. If you save $2,000, your savings rate is 50%. The higher the rate, the faster your freedom math can move.',
		},
		{
			type: 'callout',
			icon: '⏳',
			text: 'Savings rate matters because it turns money into time.',
		},
		{
			type: 'bullet_list',
			items: [
				'Higher savings builds assets faster',
				'Lower spending can reduce your FI number',
				'More surplus gives you more choices',
				'A strong rate creates distance from paycheck-to-paycheck living',
			],
		},
		{
			type: 'example',
			label: 'Same income, different path',
			text: 'Two people both take home $4,000 per month. One spends $3,800 and saves $200. The other spends $3,000 and saves $1,000. The second person is not only saving more. Their lifestyle may also require less wealth to support later.',
		},
		{
			type: 'paragraph',
			text: 'This is the dual effect. Saving more adds fuel. Spending less can shrink the target. That is why savings rate often matters more than income alone. A high income with a low savings rate can still feel trapped. A modest income with a strong savings rate can build real options.',
		},
		{
			type: 'paragraph',
			text: 'But the number has to be livable. A savings rate that makes your life feel like punishment may not last. The goal is not to flex a percentage. The goal is to create a repeatable gap between what comes in and what goes out.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is chasing a savings rate so extreme that your present self eventually rebels.',
		},
		{
			type: 'key_takeaway',
			text: 'Savings rate is freedom speed. The stronger and more livable it is, the more options you build.',
		},
	],
	audioScript:
		'Savings rate is one of the cleanest freedom numbers because it tells you how much of your income becomes future options. If you take home $4,000 a month and save $400, that is a 10% savings rate. If you save $1,000, that is 25%. If you save $2,000, that is 50%. The number matters because it does two things at once. First, it shows how fast you are building assets. Second, it gives a clue about how expensive your life is. This is the dual effect. When you save more, you add fuel. When you spend less, you may lower the target needed for freedom later. That is why someone with a huge income can still feel stuck if every dollar is already spoken for. And someone with a normal income can still build options if they keep a healthy gap between income and expenses. But do not turn this into punishment. The best savings rate is not always the biggest one you can force for one month. It is the strongest one you can repeat without hating your life. You need a plan your present self can actually live with and your future self can thank you for. Savings rate buys years because it moves money from today’s noise into tomorrow’s choices.',
	flashcards: [
		{
			id: 'freedom-math--savings-rate-buys-years--fc-main',
			role: 'main_idea',
			front: 'Why does savings rate matter?',
			back: 'It shows how much income becomes future options and how fast freedom can move.',
		},
		{
			id: 'freedom-math--savings-rate-buys-years--fc-watch',
			role: 'watch_out',
			front: 'What is the savings-rate trap?',
			back: 'Chasing an extreme rate that is not livable and eventually collapses.',
		},
		{
			id: 'freedom-math--savings-rate-buys-years--fc-use',
			role: 'use_it',
			front: 'How do you use it?',
			back: 'Build a repeatable gap between income and expenses.',
		},
	],
	applyAction: {
		type: 'analyzeSavingsRate',
		label: 'Analyze your savings rate',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'freedom-math--free-money-is-still-math',
	title: 'Free money is still math',
	description:
		'You learn how employer matching can change retirement math without treating benefits like magic.',
	pillar: 'freedom-math',
	schoolsOfThought: ['personal-finance-basics', 'fi-math', 'first-100k'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 3,
	tags: ['401k-match', 'retirement-benefits', 'employer-match', 'investing'],
	prerequisites: ['freedom-math--savings-rate-buys-years'],
	relatedLessons: ['freedom-math--late-starts-still-count'],
	concepts: ['employer-match', 'benefit-math', 'retirement-contributions'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Employer matching sounds boring until you see what it really is. It is compensation tied to a specific behavior. If an employer offers a retirement match, they are saying, “If you put in some of your pay, we may add money too, under the plan rules.”',
		},
		{
			type: 'paragraph',
			text: 'This is not magic. It is not a reason to ignore bills, debt pressure, or emergency needs. It is also not something to sleep on without understanding it. A match can change your long-term math because it adds fuel you would not otherwise receive.',
		},
		{
			type: 'callout',
			icon: '🎁',
			text: 'An employer match is part of the pay package. If you ignore it, you may be ignoring compensation.',
		},
		{
			type: 'bullet_list',
			items: [
				'Know whether a match exists',
				'Know the match formula',
				'Know the vesting rules',
				'Know your cash-flow limits',
				'Know how it fits your bigger plan',
			],
		},
		{
			type: 'example',
			label: 'Simple match example',
			text: 'Suppose someone earns $50,000 and their employer matches 50% of contributions up to 6% of pay. If they contribute 6%, that is $3,000 from them and $1,500 from the employer before investment results. The exact rules vary, but the idea is simple: the match adds outside fuel.',
		},
		{
			type: 'paragraph',
			text: 'The reason this matters in freedom math is that small advantages can stack over time. A match does not remove risk. It does not make investing simple. It does not solve every money priority. But it can improve the starting point for long-term wealth building.',
		},
		{
			type: 'paragraph',
			text: 'The adult move is to understand the benefit clearly. What is the formula? When does it vest? What account is used? What investment choices exist? What fees apply? What happens if you leave? You are not trying to become a benefits expert overnight. You are trying to stop leaving basic information unread.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is treating benefits paperwork like junk mail when it may affect your future options.',
		},
		{
			type: 'key_takeaway',
			text: 'Employer match is not magic. It is compensation math you should understand before ignoring it.',
		},
	],
	audioScript:
		'Employer match sounds like boring HR language, but it can matter a lot. The basic idea is simple. If your workplace offers a retirement match, your employer may add money when you contribute, based on the plan rules. That is part of your compensation package. It is not magic. It does not mean you ignore rent, debt pressure, emergency needs, or your actual cash flow. But it is also not something to ignore just because the paperwork is annoying. A match can add outside fuel to your long-term math. For example, if someone earns $50,000 and their employer matches 50% of contributions up to 6% of pay, a 6% contribution would be $3,000 from the worker and $1,500 from the employer before investment results. The rules vary, so the exact numbers depend on the plan. But the concept is clear: the match can change the starting point. The smart move is to understand the benefit. Does a match exist? What is the formula? Is there a vesting schedule? What are the account choices? What fees exist? What happens if you leave? You do not need to become obsessed. You just need to read the basics. Benefits are money rules attached to your job. If you ignore them, you might be ignoring part of your pay.',
	flashcards: [
		{
			id: 'freedom-math--free-money-is-still-math--fc-main',
			role: 'main_idea',
			front: 'What is an employer match?',
			back: 'Compensation your employer may add when you contribute under plan rules.',
		},
		{
			id: 'freedom-math--free-money-is-still-math--fc-watch',
			role: 'watch_out',
			front: 'What is the benefits trap?',
			back: 'Ignoring plan details because paperwork feels boring or confusing.',
		},
		{
			id: 'freedom-math--free-money-is-still-math--fc-use',
			role: 'use_it',
			front: 'What should you check?',
			back: 'Match formula, vesting, fees, account options, and cash-flow fit.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'See what a 401k match changes',
		scenarioId: '401k-match',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'freedom-math--late-starts-still-count',
	title: 'Late starts still count',
	description:
		'You learn why starting later changes the math but does not make the work pointless.',
	pillar: 'freedom-math',
	schoolsOfThought: [
		'money-psychology-housel',
		'fi-math',
		'coast-fi-slow-fi',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['late-start', 'starting-over', 'retirement', 'investing'],
	prerequisites: ['freedom-math--free-money-is-still-math'],
	relatedLessons: ['freedom-math--projections-are-not-promises'],
	concepts: ['late-start', 'catch-up-mindset', 'still-counts'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Starting earlier helps. That is true. Time gives compounding more room. But a lot of people hear that and turn it into a punishment story: “I am late, so what is the point?” That story is dangerous because it can make a delayed start even later.',
		},
		{
			type: 'paragraph',
			text: 'Late starts still count. Starting at 35, 40, 45, or later is different from starting at 22. The math may require more income, more consistency, lower expenses, longer work, or clearer priorities. But different is not the same as useless.',
		},
		{
			type: 'callout',
			icon: '🔁',
			text: 'The best time to start may have passed. The next best time is the one you still control.',
		},
		{
			type: 'bullet_list',
			items: [
				'You can still build runway',
				'You can still reduce debt pressure',
				'You can still invest consistently',
				'You can still raise income',
				'You can still make your future less fragile',
			],
		},
		{
			type: 'example',
			label: 'Starting at 40',
			text: 'Someone starting at 40 may not have the same runway as someone who started at 25. But $300, $500, or $1,000 per month directed consistently can still change their future options. The point is not to compare timelines. The point is to move the one they have.',
		},
		{
			type: 'paragraph',
			text: 'Shame is expensive here. If shame makes you avoid your numbers for five more years, it did real damage. A late start needs clarity, not self-attack. What do you have? What do you owe? What can you save? What can you earn? What lifestyle are you trying to support? Those questions create movement.',
		},
		{
			type: 'paragraph',
			text: 'Freedom math is not only about retiring early. It can also mean fewer emergencies, less panic, more choice, more stability, and a future self with better options than today’s self had.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is letting regret become another reason to delay.',
		},
		{
			type: 'key_takeaway',
			text: 'A late start changes the math. It does not erase the value of starting.',
		},
	],
	audioScript:
		'Starting early helps. There is no need to pretend otherwise. Time is powerful. But the problem is what people do with that truth. They turn it into shame. They say, “I missed my chance,” and then they avoid the numbers for another year, or five years, or ten years. That is the real danger. A late start is not the same as a pointless start. Starting at 40 is different from starting at 25. The math may require more consistency, more income, lower expenses, a longer work timeline, or clearer priorities. But it still matters. Building a few months of runway matters. Reducing debt pressure matters. Investing consistently matters. Raising income matters. Making your future less fragile matters. The point is not to compare your timeline to someone who started earlier. That comparison might teach you something once, but after that it can become poison. Your job is to move the timeline you actually have. Shame is expensive because it keeps you frozen. Clarity is better. What do you have? What do you owe? What can you save? What can you earn? What lifestyle are you trying to support? Those questions are not glamorous, but they create movement. Late starts still count because future you still exists. That person deserves better options than today’s version has.',
	flashcards: [
		{
			id: 'freedom-math--late-starts-still-count--fc-main',
			role: 'main_idea',
			front: 'Do late starts matter?',
			back: 'Yes. Starting later changes the math, but action still improves future options.',
		},
		{
			id: 'freedom-math--late-starts-still-count--fc-watch',
			role: 'watch_out',
			front: 'What makes lateness worse?',
			back: 'Shame and regret can cause more delay.',
		},
		{
			id: 'freedom-math--late-starts-still-count--fc-use',
			role: 'use_it',
			front: 'What should late starters do first?',
			back: 'Get clear on assets, debts, savings capacity, income, and target lifestyle.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'See why starting later still works',
		scenarioId: 'starting-at-40',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'freedom-math--projections-are-not-promises',
	title: 'Projections are not promises',
	description:
		'You learn how to use freedom math as a decision tool without mistaking estimates for certainty.',
	pillar: 'freedom-math',
	schoolsOfThought: [
		'fi-math',
		'money-psychology-housel',
		'boglehead-indexing',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['projections', 'assumptions', 'risk', 'financial-freedom'],
	prerequisites: ['freedom-math--late-starts-still-count'],
	relatedLessons: ['freedom-math--freedom-has-a-number'],
	concepts: ['projection-humility', 'scenario-thinking', 'assumption-risk'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Freedom math is powerful because it makes invisible tradeoffs visible. You can see how savings rate, expenses, income, investing, time, and assumptions interact. But a projection is not a promise. It is a model.',
		},
		{
			type: 'paragraph',
			text: 'A model is a simplified version of reality. It helps you think, but it cannot know your exact future. It does not know your health, job path, family needs, market returns, inflation, housing costs, tax changes, emergencies, or emotional decisions.',
		},
		{
			type: 'callout',
			icon: '🧭',
			text: 'Use projections like a compass, not a crystal ball.',
		},
		{
			type: 'bullet_list',
			items: [
				'Assumptions can be wrong',
				'Markets can disappoint',
				'Expenses can change',
				'Income can rise or fall',
				'Life can interrupt clean math',
			],
		},
		{
			type: 'example',
			label: 'One assumption changes the story',
			text: 'A plan that looks strong at one assumed return may look slower with a lower return. A plan that works with $3,500 monthly expenses may look different at $4,500. The lesson is not panic. The lesson is sensitivity.',
		},
		{
			type: 'paragraph',
			text: 'Good freedom math asks “what if?” What if returns are lower? What if rent rises? What if you save $200 more? What if you earn $500 more? What if you take a year off? What if you move? What if your spending baseline creeps up?',
		},
		{
			type: 'paragraph',
			text: 'This is where Nisk’s simulation mindset matters. You are not trying to predict your exact life. You are trying to understand which choices are powerful, which assumptions are fragile, and which tradeoffs deserve attention.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is believing the clean chart so much that you stop respecting uncertainty.',
		},
		{
			type: 'key_takeaway',
			text: 'Freedom math should make you wiser, not overconfident.',
		},
	],
	audioScript:
		'Projections are useful, but only if you treat them correctly. A projection is not a promise. It is a model. It takes your current numbers, adds assumptions, and shows a possible path. That can be extremely helpful because it makes tradeoffs visible. You can see what happens when you save more, spend less, earn more, invest longer, or change your lifestyle. But the model does not know your exact future. It does not know your health, job path, family obligations, inflation, rent, market returns, emergencies, or emotional choices. That is why you use projections like a compass, not a crystal ball. A compass helps you orient. It does not remove uncertainty. Good freedom math asks, “What if?” What if returns are lower? What if your rent rises? What if you save $200 more? What if you earn $500 more? What if your lifestyle creeps up? What if you start late but stay consistent? These questions are the real value. They help you see which choices move the needle and which assumptions are fragile. The trap is falling in love with the clean chart. Life is messier than the chart. That does not make the chart useless. It means the chart should make you humble. Freedom math should help you think better, adjust faster, and make tradeoffs clearer. It should not make you overconfident.',
	flashcards: [
		{
			id: 'freedom-math--projections-are-not-promises--fc-main',
			role: 'main_idea',
			front: 'What is a projection?',
			back: 'A model that shows a possible path based on assumptions.',
		},
		{
			id: 'freedom-math--projections-are-not-promises--fc-watch',
			role: 'watch_out',
			front: 'What is projection overconfidence?',
			back: 'Believing a clean chart so much that you ignore uncertainty.',
		},
		{
			id: 'freedom-math--projections-are-not-promises--fc-use',
			role: 'use_it',
			front: 'How should projections be used?',
			back: 'Test scenarios, compare tradeoffs, and understand fragile assumptions.',
		},
	],
	applyAction: {
		type: 'buildFreedomPlan',
		label: 'Build your freedom plan',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'freedom-math',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'freedom-math--cq--q1',
			question:
				'What is the first major input for estimating a rough FI number?',
			options: [
				'Your favorite investment app',
				'Your annual expenses',
				'Your credit score',
				'Your age only',
			],
			correctIndex: 1,
			explanation:
				'A rough FI number starts with the annual cost of the lifestyle you are trying to support.',
		},
		{
			type: 'true_false',
			id: 'freedom-math--cq--q2',
			question:
				'Your FI number should be treated as a living estimate, not a fixed destiny.',
			correct: true,
			explanation:
				'Life, expenses, markets, inflation, family needs, and goals can all change the number.',
		},
		{
			type: 'scenario',
			id: 'freedom-math--cq--q3',
			scenario:
				'Someone spends $3,000 per month and estimates annual expenses around $36,000.',
			question:
				'Using a rough 25x framework, what is their estimated FI target?',
			options: ['$90,000', '$360,000', '$900,000', '$3,600,000'],
			correctIndex: 2,
			explanation:
				'$36,000 × 25 = $900,000. This is a rough educational estimate, not a promise.',
		},
		{
			type: 'multiple_choice',
			id: 'freedom-math--cq--q4',
			question: 'Why does savings rate have a dual effect?',
			options: [
				'It raises debt and spending together',
				'It builds assets while also showing how expensive your lifestyle is',
				'It replaces the need for income',
				'It removes uncertainty',
			],
			correctIndex: 1,
			explanation:
				'Savings rate matters because it affects both the speed of asset building and the lifestyle cost that needs support.',
		},
		{
			type: 'true_false',
			id: 'freedom-math--cq--q5',
			question:
				'The highest possible savings rate is always the best plan, even if it makes life feel unbearable.',
			correct: false,
			explanation:
				'A savings rate needs to be strong and repeatable. If it feels like punishment, it may not last.',
		},
		{
			type: 'scenario',
			id: 'freedom-math--cq--q6',
			scenario:
				'Two people both take home $4,000 per month. One saves $200 and one saves $1,000.',
			question: 'What does freedom math notice?',
			options: [
				'They are on identical paths',
				'The second person is building more future options each month',
				'The first person is always happier',
				'Savings rate does not matter',
			],
			correctIndex: 1,
			explanation:
				'Saving $1,000 creates a larger gap between income and expenses, which can build options faster.',
		},
		{
			type: 'multiple_choice',
			id: 'freedom-math--cq--q7',
			question: 'What is an employer match best understood as?',
			options: [
				'A random bonus with no rules',
				'Compensation tied to retirement contribution behavior under plan rules',
				'A replacement for all savings',
				'A reason to ignore cash flow',
			],
			correctIndex: 1,
			explanation:
				'Employer match is part of the compensation package and depends on the specific plan rules.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'freedom-math--final--q1',
			question: 'What is the best definition of freedom math?',
			options: [
				'A way to predict life perfectly',
				'A way to connect lifestyle, savings, investing, income, and time',
				'A rule that everyone must retire early',
				'A method for avoiding uncertainty',
			],
			correctIndex: 1,
			explanation:
				'Freedom math helps users understand how key variables interact and what tradeoffs they create.',
		},
		{
			type: 'true_false',
			id: 'freedom-math--final--q2',
			question:
				'A higher monthly lifestyle can increase the amount of wealth needed to make work optional later.',
			correct: true,
			explanation:
				'If annual expenses rise, the rough freedom target usually rises too.',
		},
		{
			type: 'scenario',
			id: 'freedom-math--final--q3',
			scenario:
				'Riley’s monthly expenses rise from $3,000 to $4,000 after several upgrades.',
			question: 'What changed in freedom math?',
			options: [
				'The lifestyle may require a larger FI target',
				'The target automatically disappeared',
				'The upgrades have no long-term cost',
				'Riley no longer needs income',
			],
			correctIndex: 0,
			explanation:
				'Higher monthly expenses raise annual lifestyle cost, which can raise the rough FI number.',
		},
		{
			type: 'multiple_choice',
			id: 'freedom-math--final--q4',
			question:
				'Why can a high-income person still feel financially trapped?',
			options: [
				'Income never matters',
				'A low savings rate and high baseline can absorb the income',
				'High earners cannot build wealth',
				'Expenses are always fixed',
			],
			correctIndex: 1,
			explanation:
				'High income does not create freedom if the whole paycheck is already committed.',
		},
		{
			type: 'scenario',
			id: 'freedom-math--final--q5',
			scenario:
				'Someone saves aggressively for two months, hates their life, then quits the plan completely.',
			question: 'What did they miss?',
			options: [
				'Savings rate must be livable and repeatable',
				'Saving never matters',
				'Spending should always win',
				'Income is irrelevant',
			],
			correctIndex: 0,
			explanation:
				'A strong savings rate is useful only if it can survive real life.',
		},
		{
			type: 'true_false',
			id: 'freedom-math--final--q6',
			question:
				'Employer match rules can vary by plan, so users should understand the formula and vesting rules.',
			correct: true,
			explanation:
				'Match formulas, vesting, fees, and account choices can differ, so reading the plan basics matters.',
		},
		{
			type: 'scenario',
			id: 'freedom-math--final--q7',
			scenario:
				'Jordan thinks, “I did not start investing at 25, so starting at 40 is pointless.”',
			question: 'What is the best Module 10 response?',
			options: [
				'That regret should stop all action',
				'Starting later changes the math but does not make action pointless',
				'Only early starters can improve their future',
				'The math no longer matters',
			],
			correctIndex: 1,
			explanation:
				'Late starts still count. They may require clearer priorities, but action can still improve future options.',
		},
		{
			type: 'multiple_choice',
			id: 'freedom-math--final--q8',
			question: 'What makes shame expensive for late starters?',
			options: [
				'It raises market returns',
				'It can cause avoidance and more delay',
				'It improves consistency',
				'It makes the FI number smaller',
			],
			correctIndex: 1,
			explanation:
				'Shame can keep people from looking at their numbers and starting the work they still control.',
		},
		{
			type: 'scenario',
			id: 'freedom-math--final--q9',
			scenario:
				'Someone starts at 45 and focuses on building runway, reducing debt pressure, saving consistently, and improving income.',
			question: 'What is the right interpretation?',
			options: [
				'They are wasting time',
				'They are improving future options even with a later start',
				'They should only compare themselves to 22-year-olds',
				'None of it matters',
			],
			correctIndex: 1,
			explanation:
				'Later action can still create stability, options, and less fragility.',
		},
		{
			type: 'multiple_choice',
			id: 'freedom-math--final--q10',
			question: 'What does it mean to use projections like a compass?',
			options: [
				'Believe the chart exactly',
				'Use projections to orient decisions while respecting uncertainty',
				'Ignore all assumptions',
				'Treat the model as personal advice',
			],
			correctIndex: 1,
			explanation:
				'A projection helps with direction and tradeoffs, but it cannot know the exact future.',
		},
		{
			type: 'true_false',
			id: 'freedom-math--final--q11',
			question:
				'A projection can be useful even though its assumptions may be wrong.',
			correct: true,
			explanation:
				'Projections are useful for scenario thinking, sensitivity, and tradeoff awareness, not certainty.',
		},
		{
			type: 'scenario',
			id: 'freedom-math--final--q12',
			scenario:
				'A projection looks strong only if returns are high and expenses stay flat forever.',
			question: 'What should the user notice?',
			options: [
				'The plan is impossible to question',
				'The plan may be sensitive to assumptions',
				'The projection removes risk',
				'The user should ignore expense changes',
			],
			correctIndex: 1,
			explanation:
				'Freedom math should reveal which assumptions are fragile so the user can think more clearly.',
		},
	],
};

export const FREEDOM_MATH_MODULE: LearningModule = {
	id: 'freedom-math',
	title: 'Freedom Math',
	description:
		'Financial freedom becomes real when it has a number. Learn how annual expenses set your FI target, how savings rate buys years, how employer match and late starts change the math, and why projections are a compass — not a promise.',
	emoji: '🧮',
	accentColor: '#c8ff00',
	tier: 'freedom-path',
	pillar: 'freedom-math',
	order: 10,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
